import SEO from "@/components/SEO";
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface Partitura {
  id: number;
  name: string;
  description: string | null;
  price: number | null;
  discount_enabled: boolean;
  discount_price: number | null;
  status: string;
  category_id: number | null;
  artista: string | null;
  album: string | null;
  musica: string | null;
  pdf_url: string | null;
  asset_id: number | null;
}

interface Category {
  id: number;
  name: string;
}

export default function BibliotecaPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://godoyprod.com";
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [partituras, setPartituras] = useState<Partitura[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroArtista, setFiltroArtista] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [previewAberto, setPreviewAberto] = useState<number | null>(null);
  const [comprando, setComprando] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    async function carregar() {
      setLoading(true);
      const [{ data: prodData }, { data: catData }, { data: assetData }] = await Promise.all([
        supabase.from("product_items").select("*").eq("status", "active").order("id", { ascending: false }),
        supabase.from("product_categories").select("id, name").order("sort_order"),
        supabase.from("digital_assets").select("id, product_id, pdf_url, artista, album, musica"),
      ]);

      if (catData) setCategories(catData as Category[]);

      if (prodData) {
        const enriched = (prodData as any[]).map((p) => {
          const asset = (assetData || []).find((a: any) => a.product_id === p.id);
          return {
            ...p,
            artista: asset?.artista || null,
            album: asset?.album || null,
            musica: asset?.musica || null,
            pdf_url: asset?.pdf_url || null,
            asset_id: asset?.id || null,
          } as Partitura;
        });
        setPartituras(enriched);
      }
      setLoading(false);
    }
    carregar();
  }, []);

  // Lista de artistas únicos para o filtro
  const artistas = useMemo(() => {
    const set = new Set<string>();
    partituras.forEach((p) => { if (p.artista) set.add(p.artista); });
    return Array.from(set).sort();
  }, [partituras]);

  const filtradas = useMemo(() => {
    return partituras.filter((p) => {
      const matchBusca = !busca.trim() ||
        p.name.toLowerCase().includes(busca.toLowerCase()) ||
        (p.artista || "").toLowerCase().includes(busca.toLowerCase()) ||
        (p.musica || "").toLowerCase().includes(busca.toLowerCase()) ||
        (p.album || "").toLowerCase().includes(busca.toLowerCase());
      const matchArtista = !filtroArtista || p.artista === filtroArtista;
      const matchCategoria = !filtroCategoria || String(p.category_id) === filtroCategoria;
      return matchBusca && matchArtista && matchCategoria;
    });
  }, [partituras, busca, filtroArtista, filtroCategoria]);

  async function handleComprar(partitura: Partitura) {
    if (!user) {
      navigate(`/login?redirect=/biblioteca`);
      return;
    }
    if (!partitura.price || partitura.price <= 0) {
      setToast({ type: "error", msg: "Preço não definido para esta partitura." });
      return;
    }

    setComprando(partitura.id);
    try {
      const address = profile?.endereco && typeof profile.endereco === "object"
        ? profile.endereco
        : null;

      const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
        body: {
          items: [{ product_id: partitura.id, quantity: 1 }],
          user_id: user.id,
          customer_email: user.email,
          address,
        },
      });

      if (error || !data?.url) {
        setToast({ type: "error", msg: data?.error || error?.message || "Erro ao iniciar pagamento." });
      } else {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setToast({ type: "error", msg: err.message || "Erro ao processar compra." });
    } finally {
      setComprando(null);
    }
  }

  const formatPrice = (p: Partitura) => {
    const valor = p.discount_enabled && p.discount_price ? p.discount_price : p.price;
    if (!valor) return "—";
    return `R$ ${valor.toFixed(2).replace(".", ",")}`;
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Biblioteca de Partituras | GodoyProd",
    url: `${siteUrl}/biblioteca`,
    description: "Explore nossa biblioteca completa de partituras transcritas profissionalmente.",
    inLanguage: "pt-BR",
  };

  return (
    <>
      <SEO
        title="Biblioteca de Partituras"
        description="Explore nossa biblioteca completa de partituras transcritas profissionalmente. Partituras em PDF para download."
        keywords="biblioteca partituras, partituras pdf, transcrição partituras, partituras download"
        schema={schema}
        canonicalPath="/biblioteca"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Elegant%20dark%20piano%20keyboard%20close-up%20musical%20keys%20soft%20golden%20ambient%20light%20luxury%20professional%20atmosphere%20abstract%20blurred%20background&width=1920&height=800&seq=godoy-biblio-hero&orientation=landscape')] bg-cover bg-center" />
          <div className="absolute inset-0 bg-godoy-black/80" />
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto text-center">
            <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">Biblioteca</span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-godoy-cream mt-4">
              Partituras
            </h1>
            <p className="text-godoy-lightgray mt-4 max-w-2xl mx-auto">
              Partituras transcritas profissionalmente, disponíveis em PDF para download imediato após a compra.
            </p>
          </div>
        </section>

        {/* Filtros + Lista */}
        <section className="w-full px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
          <div className="max-w-6xl mx-auto">

            {/* Filtros */}
            <div className="flex flex-col md:flex-row gap-3 mb-8">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-godoy-lightgray" />
                <input
                  type="text"
                  placeholder="Buscar por nome, artista ou música..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg text-godoy-cream placeholder:text-godoy-lightgray/50 focus:outline-none focus:border-godoy-gold/50 text-sm"
                />
              </div>
              {artistas.length > 0 && (
                <select
                  value={filtroArtista}
                  onChange={(e) => setFiltroArtista(e.target.value)}
                  className="px-4 py-3 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none"
                >
                  <option value="">Todos os artistas</option>
                  {artistas.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              )}
              {categories.length > 0 && (
                <select
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  className="px-4 py-3 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none"
                >
                  <option value="">Todas as categorias</option>
                  {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
              )}
            </div>

            {/* Contador */}
            {!loading && (
              <p className="text-godoy-lightgray/60 text-xs mb-4">
                {filtradas.length} partitura{filtradas.length !== 1 ? "s" : ""} encontrada{filtradas.length !== 1 ? "s" : ""}
              </p>
            )}

            {/* Lista */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="w-10 h-10 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtradas.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-file-music-line text-5xl text-godoy-lightgray/20 mb-4" />
                <p className="text-godoy-lightgray text-sm">Nenhuma partitura encontrada.</p>
                {(busca || filtroArtista || filtroCategoria) && (
                  <button
                    onClick={() => { setBusca(""); setFiltroArtista(""); setFiltroCategoria(""); }}
                    className="mt-4 text-godoy-gold text-sm hover:underline"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg overflow-hidden">
                {filtradas.map((partitura, index) => (
                  <div key={partitura.id}>
                    <div
                      className={`flex items-center gap-4 px-6 py-4 hover:bg-godoy-charcoal/60 transition-colors cursor-pointer ${
                        index !== filtradas.length - 1 ? "border-b border-godoy-gray/10" : ""
                      } ${previewAberto === partitura.id ? "bg-godoy-charcoal/60" : ""}`}
                      onClick={() => setPreviewAberto(previewAberto === partitura.id ? null : partitura.id)}
                    >
                      {/* Ícone */}
                      <div className="w-10 h-10 rounded-lg bg-godoy-gold/10 border border-godoy-gold/20 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-music-line text-godoy-gold text-lg" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-godoy-cream font-medium text-sm truncate">{partitura.name}</p>
                        <p className="text-godoy-lightgray/60 text-xs mt-0.5 truncate">
                          {[partitura.artista, partitura.album].filter(Boolean).join(" · ") || "—"}
                        </p>
                      </div>

                      {/* Preço + seta */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {partitura.discount_enabled && partitura.discount_price && (
                          <span className="text-godoy-lightgray/50 text-xs line-through">
                            R$ {partitura.price?.toFixed(2).replace(".", ",")}
                          </span>
                        )}
                        <span className="text-godoy-gold font-semibold text-sm">
                          {formatPrice(partitura)}
                        </span>
                        <i className={`ri-arrow-${previewAberto === partitura.id ? "up" : "down"}-s-line text-godoy-lightgray/50 text-lg`} />
                      </div>
                    </div>

                    {/* Preview expandido */}
                    {previewAberto === partitura.id && (
                      <div className="px-6 py-6 bg-godoy-black/40 border-b border-godoy-gray/10">
                        <div className="flex flex-col md:flex-row gap-6 items-start">

                          {/* Miniatura PDF */}
                          <div className="w-full md:w-48 h-60 bg-godoy-charcoal/60 rounded-lg border border-godoy-gray/30 flex items-center justify-center flex-shrink-0">
                            {partitura.pdf_url ? (
                              <div className="text-center p-4">
                                <i className="ri-file-pdf-line text-4xl text-red-400/70 mb-2" />
                                <p className="text-godoy-cream text-xs font-medium">{partitura.name}</p>
                                <p className="text-godoy-lightgray/50 text-xs mt-1">PDF disponível</p>
                              </div>
                            ) : (
                              <div className="text-center p-4">
                                <i className="ri-file-music-line text-4xl text-godoy-gold/30 mb-2" />
                                <p className="text-godoy-lightgray/50 text-xs">PDF em breve</p>
                              </div>
                            )}
                          </div>

                          {/* Detalhes */}
                          <div className="flex-1">
                            <h3 className="font-serif text-xl font-bold text-godoy-cream mb-1">{partitura.name}</h3>
                            {partitura.artista && (
                              <p className="text-godoy-gold text-sm mb-3">{partitura.artista}{partitura.album ? ` · ${partitura.album}` : ""}</p>
                            )}
                            {partitura.description && (
                              <p className="text-godoy-lightgray text-sm mb-4 leading-relaxed">{partitura.description}</p>
                            )}

                            {/* Tags */}
                            <div className="flex flex-wrap gap-2 mb-5">
                              {partitura.musica && (
                                <span className="px-3 py-1 bg-godoy-gold/10 text-godoy-gold text-xs rounded-full border border-godoy-gold/20">
                                  <i className="ri-music-2-line mr-1" />{partitura.musica}
                                </span>
                              )}
                              {partitura.pdf_url && (
                                <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-full border border-emerald-500/20">
                                  <i className="ri-file-pdf-line mr-1" />PDF incluído
                                </span>
                              )}
                              <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full border border-blue-500/20">
                                <i className="ri-download-line mr-1" />Download imediato
                              </span>
                            </div>

                            {/* Preço e botão */}
                            <div className="flex items-center gap-4 flex-wrap">
                              <div>
                                {partitura.discount_enabled && partitura.discount_price ? (
                                  <div>
                                    <span className="text-godoy-lightgray/50 text-sm line-through mr-2">
                                      R$ {partitura.price?.toFixed(2).replace(".", ",")}
                                    </span>
                                    <span className="text-godoy-gold font-bold text-2xl">
                                      R$ {partitura.discount_price.toFixed(2).replace(".", ",")}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-godoy-gold font-bold text-2xl">
                                    {formatPrice(partitura)}
                                  </span>
                                )}
                              </div>

                              <button
                                onClick={(e) => { e.stopPropagation(); handleComprar(partitura); }}
                                disabled={comprando === partitura.id}
                                className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-bold rounded-lg hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60 flex items-center gap-2 whitespace-nowrap"
                              >
                                {comprando === partitura.id ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-godoy-black border-t-transparent rounded-full animate-spin" />
                                    Processando...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-shopping-cart-line" />
                                    Comprar agora
                                  </>
                                )}
                              </button>

                              {!user && (
                                <Link
                                  to="/login"
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-5 py-2.5 border border-godoy-gold/40 text-godoy-gold rounded-lg hover:bg-godoy-gold/10 transition-all text-sm whitespace-nowrap"
                                >
                                  Entrar para comprar
                                </Link>
                              )}
                            </div>

                            <p className="text-godoy-lightgray/40 text-xs mt-3">
                              <i className="ri-instagram-line mr-1" />
                              Comprou? Marque <strong className="text-godoy-gold/60">@eugoodoy</strong> no Instagram!
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-[150] max-w-sm">
            <div className={`px-5 py-3 rounded-md text-sm font-medium border flex items-center gap-2 ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-red-500/10 border-red-500/30 text-red-400"
            }`}>
              <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
              {toast.msg}
              <button onClick={() => setToast(null)} className="ml-2 text-godoy-lightgray hover:text-godoy-cream">
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}